import React, {useCallback, useRef, useState} from "react";
import Api from "../../../../core/services/api/api";
import {getActionablePartFormQualificationForms, isPartFormQualificationEnabled,} from "./use-part-form-qualification";
import type {PartFormQualificationDs} from "../../../../core/services/api/type-declerations/response";
import {fetchPartFormQualification} from "../utils/fetch-part-form-qualification";
import type {PartFormQualificationErrorInfo} from "../utils/parse-part-form-qualification-error";
import PartFormComplianceDialog from "../components/part-form-compliance-dialog";
import PartFormQualificationFetchErrorDialog from "../components/part-form-qualification-fetch-error-dialog";

export interface PartFormComplianceDialogEntry {
    partId: number | string;
    partLabel?: string;
    forms: import("../../../../core/services/api/type-declerations/response").PartFormQualificationFormDs[];
}

export interface PartFormQualificationGateTarget {
    partId: number | string;
    customerId: number | string;
    partLabel?: string;
}

export interface UsePartFormQualificationGateResult {
    checking: boolean;
    ensureQualified: (target: PartFormQualificationGateTarget) => Promise<boolean>;
    ensureAllQualified: (customerId: number | string, targets: Array<{ partId?: number | string | null; partNumber?: string | null; partLabel?: string }>) => Promise<boolean>;
    GateDialogs: React.ReactNode;
}

/**
 * Resolves the part ID from the given part number by calling the API. Returns the part ID if found, or null if not found or if there was an error.
 * @param partNumber
 */
async function resolvePartIdFromPartNumber(partNumber: string): Promise<number | null> {
    const response = await Api.getPartInfoWithPartNumber({partNumbers: [partNumber]}, false);
    if (!response?.resultFlag || !response.data?.length) {
        return null;
    }
    const match = response.data.find((e: { partNo?: string }) => e.partNo === partNumber);
    return match?.id ?? null;
}

/**
 * Builds the entries for the compliance dialog from the qualification check results.
 * Each entry corresponds to an unqualified target and includes the part ID, part label, and the actionable forms that the user can fill out to comply with the qualification.
 * @param results
 */
function buildUnqualifiedEntries(
    results: Array<{ partId: number | string; partLabel?: string; qualification: PartFormQualificationDs | null }>,
): PartFormComplianceDialogEntry[] {
    return results
        .filter(({qualification}) => qualification && !qualification.qualified)
        .map(({partId, partLabel, qualification}) => ({
            partId,
            partLabel,
            forms: getActionablePartFormQualificationForms(qualification?.forms),
        }))
        .filter((entry) => entry.forms.length > 0);
}

export function usePartFormQualificationGate(): UsePartFormQualificationGateResult {
    const [checking, setChecking] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [dialogEntries, setDialogEntries] = useState<PartFormComplianceDialogEntry[]>([]);
    const [fetchErrorOpen, setFetchErrorOpen] = useState(false);
    const [fetchErrorLoading, setFetchErrorLoading] = useState(false);
    const [fetchErrorState, setFetchErrorState] = useState<{
        customerId: number | string;
        errorInfo: PartFormQualificationErrorInfo | null;
    } | null>(null);
    const [dialogContext, setDialogContext] = useState<{
        customerId: number | string;
        targets: PartFormQualificationGateTarget[];
    } | null>(null);
    const resolverRef = useRef<((value: boolean) => void) | null>(null);
    const retryContextRef = useRef<{
        customerId: number | string;
        targets: PartFormQualificationGateTarget[];
    } | null>(null);

    /**
     * Closes the compliance dialog, clears the dialog context and entries, and resolves the gate with the given value.
     * This is used when the user interacts with the compliance dialog to either comply with all unqualified targets (resolved with true) or choose not to comply with any unqualified target
     * (resolved with false), as well as when the user closes the dialog without making a choice (resolved with false).
     */
    const closeDialog = useCallback((resolved: boolean) => {
        setDialogOpen(false);
        setDialogEntries([]);
        setDialogContext(null);
        resolverRef.current?.(resolved);
        resolverRef.current = null;
    }, []);

    /**
     * Closes the fetch error dialog, clears the retry context, and resolves the gate with the given value.
     * This is used when the user closes the fetch error dialog without retrying, or after a successful retry.
     */
    const closeFetchErrorDialog = useCallback((resolved: boolean) => {
        setFetchErrorOpen(false);
        setFetchErrorState(null);
        retryContextRef.current = null;
        resolverRef.current?.(resolved);
        resolverRef.current = null;
    }, []);

    /**
     * Runs the qualification checks for the given targets and returns the results. Each result includes the original target,
     * the fetched qualification (if successful), and any fetch error that occurred.
     */
    const runQualificationChecks = useCallback(async (customerId: number | string, targets: PartFormQualificationGateTarget[]) => {
        return Promise.all(
            targets.map(async (target) => {
                const {qualification, error, errorInfo} = await fetchPartFormQualification(target.partId, customerId);
                return {
                    ...target,
                    qualification,
                    fetchError: error,
                    fetchErrorInfo: errorInfo,
                };
            }),
        );
    }, []);

    /**
     * Refreshes the qualification checks for the targets in the current dialog context, and if all targets are qualified, resolves the gate. If there are unqualified targets,
     * updates the dialog entries with the new unqualified targets. If there is a fetch error, closes the dialog and opens the fetch error dialog.
     */
    const refreshDialogQualification = useCallback(async () => {
        if (!dialogContext) {
            return;
        }
        setDialogLoading(true);
        const results = await runQualificationChecks(dialogContext.customerId, dialogContext.targets);
        if (results.some(({fetchError}) => fetchError)) {
            setDialogLoading(false);
            setDialogOpen(false);
            retryContextRef.current = dialogContext;
            const failed = results.find(({fetchError}) => fetchError);
            setFetchErrorState({
                customerId: dialogContext.customerId,
                errorInfo: failed?.fetchErrorInfo ?? null,
            });
            setFetchErrorOpen(true);
            return;
        }
        const allQualified = results.every(({qualification}) => qualification?.qualified);
        if (allQualified) {
            setDialogLoading(false);
            closeDialog(true);
            return;
        }
        setDialogEntries(buildUnqualifiedEntries(results));
        setDialogLoading(false);
    }, [closeDialog, dialogContext, runQualificationChecks]);

    /**
     * Opens the compliance dialog for the given unqualified targets, and sets up the resolver for resolving the gate after the user interacts with the dialog.
     * The resolver will be resolved with true if the user complies with all unqualified targets, false if the user chooses not to comply with any unqualified target, and will remain unresolved if the user closes the dialog without making a choice.
     */
    const openComplianceDialog = useCallback((customerId: number | string, targets: PartFormQualificationGateTarget[], entries: PartFormComplianceDialogEntry[]) =>
        new Promise<boolean>((resolve) => {
            resolverRef.current = resolve;
            setDialogContext({customerId, targets});
            setDialogEntries(entries);
            setDialogOpen(true);
        }), []);

    /**
     * Opens the fetch error dialog, and sets up the resolver and retry context for retrying the qualification checks after a fetch error.
     * The retry context will be used to retry the qualification checks with the same parameters when the user clicks the retry button in the fetch error dialog.
     */
    const openFetchErrorDialog = useCallback((
        customerId: number | string,
        targets: PartFormQualificationGateTarget[],
        errorInfo: PartFormQualificationErrorInfo | null,
    ) =>
        new Promise<boolean>((resolve) => {
            resolverRef.current = resolve;
            retryContextRef.current = {customerId, targets};
            setFetchErrorState({customerId, errorInfo});
            setFetchErrorOpen(true);
        }), []);

    /**
     * Retries the qualification checks after a fetch error, and if successful, resolves the gate. If there are still unqualified targets, opens the compliance dialog.
     * If there is another fetch error, keeps the fetch error dialog open.
     */
    const retryAfterFetchError = useCallback(async () => {
        const context = retryContextRef.current;
        if (!context) {
            return;
        }
        setFetchErrorLoading(true);
        const results = await runQualificationChecks(context.customerId, context.targets);
        setFetchErrorLoading(false);

        if (results.some(({fetchError}) => fetchError)) {
            const failed = results.find(({fetchError}) => fetchError);
            setFetchErrorState({
                customerId: context.customerId,
                errorInfo: failed?.fetchErrorInfo ?? null,
            });
            return;
        }

        setFetchErrorOpen(false);
        setFetchErrorState(null);

        if (results.every(({qualification}) => qualification?.qualified)) {
            retryContextRef.current = null;
            resolverRef.current?.(true);
            resolverRef.current = null;
            return;
        }

        const entries = buildUnqualifiedEntries(results);
        retryContextRef.current = null;
        if (!entries.length) {
            resolverRef.current?.(false);
            resolverRef.current = null;
            return;
        }

        return openComplianceDialog(context.customerId, context.targets, entries);
    }, [openComplianceDialog, runQualificationChecks]);

    /**
     * Runs the qualification gate for the given targets, and if any target is unqualified, opens the compliance dialog for the unqualified targets.
     * The partId can be resolved from partNumber if not provided.
     */
    const runGate = useCallback(async (customerId: number | string, targets: PartFormQualificationGateTarget[]): Promise<boolean> => {
        const validTargets = targets.filter((t) => isPartFormQualificationEnabled(t.partId, customerId));
        if (!validTargets.length) {
            return true;
        }
        setChecking(true);
        const results = await runQualificationChecks(customerId, validTargets);
        setChecking(false);

        if (results.some(({fetchError}) => fetchError)) {
            const failed = results.find(({fetchError}) => fetchError);
            return openFetchErrorDialog(customerId, validTargets, failed?.fetchErrorInfo ?? null);
        }

        if (results.every(({qualification}) => qualification?.qualified)) {
            return true;
        }

        const entries = buildUnqualifiedEntries(results);
        if (!entries.length) {
            return false;
        }

        return openComplianceDialog(customerId, validTargets, entries);
    }, [openComplianceDialog, openFetchErrorDialog, runQualificationChecks]);

    /**
     * Ensures that the given target is qualified, and if not, opens the compliance dialog for the unqualified target. The partId can be resolved from partNumber if not provided.
     */
    const ensureQualified = useCallback(async ({partId, customerId, partLabel}: PartFormQualificationGateTarget) =>
            runGate(customerId, [{partId, customerId, partLabel}]),
        [runGate]);

    /**
     * Ensures that the given targets are qualified, and if not, opens the compliance dialog for the unqualified targets. The partId can be resolved from partNumber if not provided.
     * Returns true if all targets are qualified or become qualified after user interaction, false if there are unqualified targets that the user chose not to comply with,
     * and null if the qualification check could not be completed due to a fetch error.
     */
    const ensureAllQualified = useCallback(async (
        customerId: number | string,
        targets: Array<{ partId?: number | string | null; partNumber?: string | null; partLabel?: string }>,
    ) => {
        const resolved: PartFormQualificationGateTarget[] = [];
        for (const target of targets) {
            let partId = target.partId;
            const partLabel = target.partLabel ?? target.partNumber ?? undefined;
            if ((partId == null || partId === "") && target.partNumber) {
                partId = await resolvePartIdFromPartNumber(target.partNumber);
            }
            if (partId != null && partId !== "") {
                resolved.push({partId, customerId, partLabel});
            }
        }
        const uniqueByPartId = Array.from(
            new Map(resolved.map((t) => [String(t.partId), t])).values(),
        );
        return runGate(customerId, uniqueByPartId);
    }, [runGate]);

    const GateDialogs = (
        <>
            <PartFormComplianceDialog
                open={dialogOpen}
                onClose={() => closeDialog(false)}
                entries={dialogEntries}
                onQualificationRefresh={refreshDialogQualification}
                loading={dialogLoading}
            />
            <PartFormQualificationFetchErrorDialog
                open={fetchErrorOpen}
                onClose={() => closeFetchErrorDialog(false)}
                onRetry={() => void retryAfterFetchError()}
                loading={fetchErrorLoading}
                errorMessage={fetchErrorState?.errorInfo?.message}
                customerId={fetchErrorState?.customerId}
                showCustomerProfileButton={fetchErrorState?.errorInfo?.isCustomerEmailError}
            />
        </>
    );

    return {
        checking,
        ensureQualified,
        ensureAllQualified,
        GateDialogs,
    };
}
